import { Bodies, Constraint } from 'matter-js';
import { BubbleClusterComponent } from '../components/BubbleCluster';
import { createClusterConstraintAtIndex } from '../physics/constraints';
import { BUBBLE_DEFAULT_RADIUS, BUBBLE_SCALE_FACTOR, COLLISION_CATEGORIES } from '../utils/constants';
import { AbstractEntity } from './AbstractEntity';
import { BubbleEntity } from './BubbleEntity';

interface ClusterEntityConfig {
    bubbles: BubbleEntity[];
    x: number;
    y: number;
    primaryBubble?: BubbleEntity | null; // La burbuja "central" que se muestra al frente (opcional)
}

export class ClusterEntity extends AbstractEntity {
    private internalBubbles: BubbleEntity[] = [];
    private constraints: Constraint[] = [];
    private primaryBubble: BubbleEntity | null = null;

    constructor(config: ClusterEntityConfig) {
        const { bubbles, x, y, primaryBubble } = config;
        // Calcular radio del cluster basado en el radio real de las burbujas internas
        // (dinámico según el tamaño de pantalla), con fallback al radio de referencia.
        const referenceRadius = bubbles[0]?._radius || BUBBLE_DEFAULT_RADIUS;
        const radius = referenceRadius * (BUBBLE_SCALE_FACTOR + 0.4);

        // Crear el cuerpo físico del Cluster (El Contenedor)
        const body = Bodies.circle(x, y, radius, {
            label: 'CLUSTER',
            // isSensor: true, // El cluster no choca físicamente, solo detecta superposiciones
            frictionAir: 0.5, // Más pesado que las burbujas (para ganarles interactuar con los constraints)
            density: 0.01,
            // restitution: 0.2,
            collisionFilter: {
                category: COLLISION_CATEGORIES.CLUSTER,
                // Debe chocar con: DEFAULT (paredes), BUBBLE (burbujas sueltas), CLUSTER (otros clusters)
                mask: COLLISION_CATEGORIES.CLUSTER // COLLISION_CATEGORIES.DEFAULT | COLLISION_CATEGORIES.BUBBLE | COLLISION_CATEGORIES.CLUSTER
            },
        });

        // Crear elemento DOM
        const element = new BubbleClusterComponent(); //document.createElement('div');
        element.classList.add('cluster-node');
        element.k_length = bubbles.length;
        element.style.width = `${radius * 2}px`;
        element.style.height = `${radius * 2}px`;
        // Accesibilidad: identifica el grupo como una unidad (no navegable individualmente por teclado,
        // ya que arrastrar el cluster solo es soportado por mouse/touch)
        element.setAttribute('role', 'group');

        super(element, body, undefined);

        this.internalBubbles = bubbles;
        this.primaryBubble = primaryBubble || null;
        this.captureBubbles();
        this.updateAccessibleLabel();

        this.bringPrimaryBubbleToFront(); // Traer la burbuja "central" al frente si existe
    }

    private updateAccessibleLabel() {
        const count = this.internalBubbles.length;
        this._element.setAttribute('aria-label', `Grupo de ${count} burbuja${count === 1 ? '' : 's'}`);
    }

    // *************************************************************************
    //#region PRIVATE METHODS

    private captureBubbles() {
        const bubbles = this.internalBubbles;

        this.constraints = []; // resetear constraints anteriores

        // Asegurar orden correcto de apilado: el cluster detrás de las burbujas internas
        for (let i = bubbles.length - 1; i >= 0; i--) {
            const bubble = bubbles[i];

            bubble._element.parentElement?.appendChild(bubble._element); // traer al frente iterativamente para mantener el orden correcto dentro del cluster

            this.applyBubbleState(bubble, true);

            const constraint = createClusterConstraintAtIndex(this._body, bubbles, i);
            this.constraints.push(constraint);
        }

        // Crear constraints para mantener las burbujas atrapadas en el cluster
        // this.constraints = createClusterConstraints(this._body, bubbles);
    }

    private applyBubbleState(bubble: BubbleEntity, trapped: boolean) {
        if (trapped) {
            bubble._CancelLaunch();

            bubble._SetTrappedState(true);
            bubble._SetCollisionsEnabled(false);
        } else {
            bubble._SetTrappedState(false);
            bubble._SetCollisionsEnabled(true);
        }
    }

    private applyReleaseBubbles(bubbles: BubbleEntity[], lastPosition?: Record<string, { x: number, y: number }>) {
        bubbles.forEach((bubble: BubbleEntity) => {
            this.applyBubbleState(bubble, false);

            const lastPos = lastPosition?.[bubble._id];
            if (lastPos) {
                bubble._LaunchTo(lastPos.x, lastPos.y, 0.5, false);
            }
        });
    }

    private bringPrimaryBubbleToFront() {
        if (!this.primaryBubble) return;
        this.primaryBubble._element.classList.add('in-front');
    }

    private sendPrimaryBubbleToBack() {
        if (!this.primaryBubble) return;
        this.primaryBubble._element.classList.remove('in-front');
    }

    //#endregion
    // *************************************************************************

    // *************************************************************************
    //#region API

    public get _internalBubbles(): BubbleEntity[] {
        return this.internalBubbles;
    }

    public get _primaryBubble(): BubbleEntity | null {
        return this.primaryBubble;
    }

    public _GetConstraints() {
        return this.constraints;
    }

    /**
     * Devuelve todo lo que hay que agregar al World
     * (El cuerpo del cluster + los constraints nuevos)
     */
    public _GetPhysicsComposite(): any[] {
        return [this._body, ...this.constraints];
    }

    /** Libera todas las burbujas internas del cluster */
    public _ReleaseBubbles(lastPosition?: Record<string, { x: number, y: number }>) {
        this.sendPrimaryBubbleToBack();

        this.applyReleaseBubbles(this.internalBubbles, lastPosition);
        this.internalBubbles = [];
    }

    /**
     * Actualiza las burbujas internas del cluster.
     * Nota: Solo actualiza las referencias, NO recalcula ni crea los constraints.
     */
    public _SetBubbles(bubbles: BubbleEntity[], primaryBubble?: BubbleEntity | null) {
        this.sendPrimaryBubbleToBack(); // manda burbuja primaria anterior al fondo antes de actualizar
        this.internalBubbles = bubbles;
        this.primaryBubble = primaryBubble || null;
        (this._element as BubbleClusterComponent).k_length = bubbles.length;
        this.updateAccessibleLabel();
    }

    /** Captura las burbujas internas del cluster y las trae al frente si existe una burbuja primaria
     * Nota: No debe invocarse más de una vez sin antes actualizar las burbujas internas,
     * porque no recalcula ni crea los constraints, solo reaplica el estado atrapado y el orden visual
     */
    public _CaptureBubbles() {
        this.captureBubbles();
        this.bringPrimaryBubbleToFront(); // Traer la burbuja "central" al frente si existe
    }

    public override _Sync(): void {
        super._Sync();
    }

    // Sobrescribimos destroy para liberar las burbujas
    public override _Destroy(): void {
        this._ReleaseBubbles();
        super._Destroy();
    }

    //#endregion
    // *************************************************************************
}